import { Cartesian3, createGuid } from "cesium";
import { CurveDataType, HorizontalAlignmentType, } from "../core/alignment/horizontal-alignment/interface";
import { QuadraticBezierHorizontalAlignment } from "../core/alignment/horizontal-alignment/quadratic-bezier-horizontal-alignment";
import { Site } from "../site/site";
import { SegmentType } from "../types";
export async function getHorizontalAlignment(surveyPoints, horizontalAlignmentCurveData) {
    const site = Site.getInstance();
    const wgs84Points = surveyPoints.map((p) => p.wgs84Point);
    const localPoints = await site.convertPointsToLocalSpace(wgs84Points);
    const surveyPointsMap = new Map(surveyPoints.map((p, i) => [p.id, { ...p, localPoint: localPoints[i] }]));
    const horizontalAlignmentData = [];
    for (const ha of horizontalAlignmentCurveData) {
        switch (ha.type) {
            case CurveDataType.LINEAR:
                {
                    horizontalAlignmentData.push({
                        startPointId: ha.startPointId,
                        endPointId: ha.endPointId,
                        type: CurveDataType.LINEAR,
                    });
                }
                break;
            case CurveDataType.QUADRATIC_BEZIER:
                {
                    horizontalAlignmentData.push({
                        type: CurveDataType.QUADRATIC_BEZIER,
                        pointIds: ha.pointIds,
                        startPointId: ha.startPointId,
                        endPointId: ha.endPointId,
                    });
                }
                break;
            case CurveDataType.THREE_POINT_ARC:
                {
                    horizontalAlignmentData.push({
                        startPointId: ha.startPointId,
                        throughPointId: ha.throughPointId,
                        endPointId: ha.endPointId,
                        type: CurveDataType.THREE_POINT_ARC,
                    });
                }
                break;
            default:
                throw new Error(`Unknown curve type: ${ha.type}`);
        }
    }
    const horizontalAlignment = new QuadraticBezierHorizontalAlignment();
    horizontalAlignment.initializeFromCurveData(horizontalAlignmentData, surveyPointsMap);
    return horizontalAlignment;
}
export async function getAlignmentDataFromControlPointsAndSegments(json, offset = 0) {
    const surveyPoints = json.controlPoints.map((p, i) => ({
        wgs84Point: Cartesian3.fromArray(p.point),
        id: createGuid(),
        name: "IP " + i,
    }));
    const ipPointIds = surveyPoints.map((p) => p.id);
    const horizontalAlignmentData = computeHorizontalAlignmentData(json.segments.map((s) => s.type), ipPointIds, surveyPoints);
    const horizontalAlignment = await getHorizontalAlignment(surveyPoints, horizontalAlignmentData);
    const horizontalLength = horizontalAlignment.length;
    const verticalAlignmentData = json.slopeBreaks.map((b) => {
        return {
            elevation: b.height - offset,
            horizontalDistance: b.percentage * horizontalLength,
        };
    });
    return {
        surveyPoints,
        ipPointIds,
        horizontalAlignmentData: {
            type: HorizontalAlignmentType.QUADRATIC_BEZIER,
            data: horizontalAlignmentData,
        },
        verticalAlignmentData,
    };
}
export async function getAlignmentDataFromControlPointsAndSegmentTypes(data) {
    const surveyPoints = data.controlPoints.map((p, index) => ({
        wgs84Point: new Cartesian3(p.point.x, p.point.y, p.point.z),
        id: createGuid(),
        name: "IP " + index,
    }));
    const ipPointIds = surveyPoints.map((p) => p.id);
    const horizontalAlignmentData = computeHorizontalAlignmentData(data.segmentTypes, ipPointIds, surveyPoints);
    // const segmentTypes = data.segmentTypes;
    const horizontalAlignment = await getHorizontalAlignment(surveyPoints, horizontalAlignmentData);
    const horizontalLength = horizontalAlignment.length;
    const slopeBreakWithAssociatedIPPoints = data.slopeBreaks.filter((b) => b.associatedControlPointIndex !== undefined);
    const slopeBreakWithoutAssociatedIPPoints = data.slopeBreaks.filter((b) => b.associatedControlPointIndex === undefined);
    const surveyPointsOfAssociatedIPPoints = slopeBreakWithAssociatedIPPoints.map((b) => surveyPoints[b.associatedControlPointIndex]);
    const wgs84Points = surveyPointsOfAssociatedIPPoints.map((p) => p.wgs84Point);
    const localPoints = await Site.getInstance().convertPointsToLocalSpace(wgs84Points);
    const horizontalDistances = localPoints.map((p) => {
        const closestPointInfo = horizontalAlignment.getClosestPointInfo(p);
        return closestPointInfo.distanceFromStart;
    });
    const verticalAlignmentData = slopeBreakWithAssociatedIPPoints.map((b, i) => {
        return {
            elevation: b.height,
            horizontalDistance: horizontalDistances[i],
        };
    });
    for (const slopeBreak of slopeBreakWithoutAssociatedIPPoints) {
        const horizontalDistance = slopeBreak.percentage * horizontalLength;
        verticalAlignmentData.push({
            elevation: slopeBreak.height,
            horizontalDistance: horizontalDistance,
        });
    }
    return {
        surveyPoints,
        ipPointIds,
        horizontalAlignmentData: {
            type: HorizontalAlignmentType.QUADRATIC_BEZIER,
            data: horizontalAlignmentData,
        },
        verticalAlignmentData,
    };
}
export function computeHorizontalAlignmentData(segmentTypes, ipPointIds, surveyPoints) {
    const horizontalAlignmentData = [];
    // if there are arcs, arc should be present in pairs, otherwise it should be converted to curve
    for (let i = 0; i < segmentTypes.length; i++) {
        if (segmentTypes[i] === SegmentType.ARC) {
            if (i + 1 < segmentTypes.length &&
                segmentTypes[i + 1] === SegmentType.ARC) {
                i++;
            }
            else {
                segmentTypes[i] = SegmentType.CURVE;
            }
        }
    }
    // if there are curves, curve cannot be single, there should be at least two consecutive curves, otherwise it should be converted to straight
    for (let i = 0; i < segmentTypes.length;) {
        if (segmentTypes[i] === SegmentType.CURVE) {
            let j = i + 1;
            while (j < segmentTypes.length && segmentTypes[j] === SegmentType.CURVE) {
                j++;
            }
            if (j - i < 2) {
                // for (let k = i; k < j; k++) {
                segmentTypes[i] = SegmentType.STRAIGHT;
                // }
            }
            i = j;
        }
        else {
            i++;
        }
    }
    // if there are straight, it can be single or multiple consecutive
    let i = 0;
    while (i < segmentTypes.length) {
        switch (segmentTypes[i]) {
            case SegmentType.STRAIGHT:
                horizontalAlignmentData.push({
                    type: CurveDataType.LINEAR,
                    startPointId: ipPointIds[i],
                    endPointId: ipPointIds[i + 1],
                });
                break;
            case "arc":
                horizontalAlignmentData.push({
                    type: CurveDataType.THREE_POINT_ARC,
                    startPointId: ipPointIds[i],
                    throughPointId: ipPointIds[i + 1],
                    endPointId: ipPointIds[i + 2],
                });
                i++;
                break;
            case "curve":
                if (horizontalAlignmentData[horizontalAlignmentData.length - 1]?.type ===
                    CurveDataType.QUADRATIC_BEZIER) {
                    horizontalAlignmentData[horizontalAlignmentData.length - 1].pointIds.push(ipPointIds[i]);
                    if (i + 1 === segmentTypes.length ||
                        segmentTypes[i + 1] !== "curve") {
                        const firstPoint = surveyPoints[i].wgs84Point;
                        const secondPoint = surveyPoints[i + 1].wgs84Point;
                        const midPoint = Cartesian3.midpoint(firstPoint, secondPoint, new Cartesian3());
                        const midPoint_SurveyPoint = {
                            wgs84Point: midPoint,
                            id: createGuid(),
                            name: "EC",
                        };
                        surveyPoints.push(midPoint_SurveyPoint);
                        horizontalAlignmentData[horizontalAlignmentData.length - 1].pointIds.push(midPoint_SurveyPoint.id);
                        horizontalAlignmentData.push({
                            type: CurveDataType.LINEAR,
                            startPointId: midPoint_SurveyPoint.id,
                            endPointId: ipPointIds[i + 1],
                        });
                    }
                }
                else {
                    const firstPoint = surveyPoints[i].wgs84Point;
                    const secondPoint = surveyPoints[i + 1].wgs84Point;
                    const midPoint = Cartesian3.midpoint(firstPoint, secondPoint, new Cartesian3());
                    const midPoint_SurveyPoint = {
                        wgs84Point: midPoint,
                        id: createGuid(),
                        name: "BC",
                    };
                    surveyPoints.push(midPoint_SurveyPoint);
                    horizontalAlignmentData.push({
                        type: CurveDataType.LINEAR,
                        startPointId: ipPointIds[i],
                        endPointId: midPoint_SurveyPoint.id,
                    });
                    horizontalAlignmentData.push({
                        type: CurveDataType.QUADRATIC_BEZIER,
                        pointIds: [midPoint_SurveyPoint.id],
                        startPointId: midPoint_SurveyPoint.id,
                        endPointId: midPoint_SurveyPoint.id,
                    });
                }
                break;
            default:
                throw new Error("This should not happen! Please use the quadratic bezier alignment generator.");
        }
        i++;
    }
    return horizontalAlignmentData;
}
//# sourceMappingURL=alignment-adapter-utils.js.map